import {Time} from 'yougile-util'
import {
  Schedule,
  SelectedDayOfMonth,
  updateDefaultScheduleProps
} from './schedule'

export const getNextScheduledTime = (
  schedule: Schedule,
  last: number
): number | undefined => {
  updateDefaultScheduleProps(schedule)
  const {since, each} = schedule
  if (!each || !since) {
    return undefined
  }

  switch (schedule.type) {
    case 'daily': {
      if (last < since) {
        return since
      }
      const step = each * Time.day
      return (Math.floor((last - since) / step) + 1) * step + since
    }
    case 'weekly': {
      if (!schedule.daysOfWeek?.length) {
        return undefined
      }

      let time = since
      let weekDay = getWeekDay(time)
      while (last >= time || !schedule.daysOfWeek.includes(weekDay)) {
        const nextWeek = weekDay !== 6 ? 0 : (each - 1) * Time.week
        time += Time.day + nextWeek
        weekDay = getWeekDay(time)
      }
      return time
    }
    case 'monthly': {
      const getNextDay = (time: number): number => {
        if (!isLastDayOfMonth(time)) {
          return time + Time.day
        }
        const date = Time.toLocalDate(time)
        date.setDate(1)
        date.setMonth(date.getMonth() + each)
        return Time.toTimestamp(date)
      }
      return getNextTimeForMonth(schedule, last, getNextDay)
    }
    case 'yearly': {
      const {monthsOfYear} = schedule
      if (!monthsOfYear?.length) {
        return undefined
      }
      const monthsSorted = [...monthsOfYear].sort((a, b) => a - b)
      const date = Time.toLocalDate(since)
      const currentMonth = date.getMonth() + 1
      let doNotSkipMonth = monthsSorted.includes(currentMonth)

      const getNextDay = (time: number): number => {
        if (doNotSkipMonth && !isLastDayOfMonth(time)) {
          return time + Time.day
        }
        doNotSkipMonth = true
        const date = Time.toLocalDate(time)
        const currentMonth = date.getMonth() + 1
        const nextMonthIndex = monthsSorted.findIndex(m => m > currentMonth)
        date.setDate(1)
        if (nextMonthIndex < 0) {
          date.setMonth(monthsSorted[0] - 1)
          date.setFullYear(date.getFullYear() + each)
        } else {
          date.setMonth(monthsSorted[nextMonthIndex] - 1)
        }
        return Time.toTimestamp(date)
      }
      return getNextTimeForMonth(schedule, last, getNextDay)
    }
    default:
      return undefined
  }
}

const getDayOfWeek = (date: Date): number => {
  const weekDayFromSunday = date.getDay()
  return weekDayFromSunday === 0 ? 6 : weekDayFromSunday - 1
}

export const getWeekDay = (time: number): number => {
  return getDayOfWeek(Time.toLocalDate(time))
}

const getNextTimeForMonth = (
  schedule: Schedule,
  last: number,
  getNextDay: (time: number) => number
): number | undefined => {
  const getDayInfo = (time: number) => {
    const date = Time.toLocalDate(time)
    return {dayOfMonth: date.getDate(), dayOfWeek: getDayOfWeek(date)}
  }

  let time = getNextDayAfterTheLast(
    getNextDay,
    schedule.since ?? last - Time.day,
    last
  )

  if (schedule.selectedDayOfMonth) {
    return getNextSelectedDayOfMonth(
      time,
      schedule.selectedDayOfMonth,
      getDayInfo,
      getNextDay
    )
  }

  if (schedule.daysOfMonth?.length) {
    let {dayOfMonth} = getDayInfo(time)
    while (
      !(
        schedule.daysOfMonth.includes(dayOfMonth) ||
        (isLastDayOfMonth(time) &&
          schedule.daysOfMonth.some(day => day > dayOfMonth))
      )
    ) {
      time = getNextDay(time)
      const info = getDayInfo(time)
      dayOfMonth = info.dayOfMonth
    }
    return time
  }

  return undefined
}

const getNextSelectedDayOfMonth = (
  time: number,
  selectedDayOfMonth: SelectedDayOfMonth,
  getDayInfo: (time: number) => {dayOfMonth: number; dayOfWeek: number},
  getNextDay: (time: number) => number
): number => {
  const {dayType} = selectedDayOfMonth

  if (dayType === 'day') {
    return getNextSelectedDay(time, selectedDayOfMonth, getDayInfo, getNextDay)
  }

  if (dayType === 'weekday') {
    return getNextSelectedWeekday(
      time,
      selectedDayOfMonth,
      getDayInfo,
      getNextDay
    )
  }

  if (dayType === 'weekend') {
    return getNextSelectedWeekend(
      time,
      selectedDayOfMonth,
      getDayInfo,
      getNextDay
    )
  }

  if (typeof dayType === 'number') {
    return getNextSelectedSpecificWeekday(
      time,
      selectedDayOfMonth,
      getDayInfo,
      getNextDay
    )
  }

  throw new Error('Not implemented for day type ' + dayType)
}

const getNextSelectedDay = (
  time: number,
  selectedDayOfMonth: SelectedDayOfMonth,
  getDayInfo: (time: number) => {dayOfMonth: number; dayOfWeek: number},
  getNextDay: (time: number) => number
): number => {
  const {dayNumber} = selectedDayOfMonth
  let {dayOfMonth} = getDayInfo(time)

  const daysOfMonth = dayNumber === 'last' ? [31] : [dayNumber]
  while (
    !(
      daysOfMonth.includes(dayOfMonth) ||
      (isLastDayOfMonth(time) && daysOfMonth.some(day => day > dayOfMonth))
    )
  ) {
    time = getNextDay(time)
    dayOfMonth = getDayInfo(time).dayOfMonth
  }
  return time
}

const getNextSelectedWeekday = (
  time: number,
  selectedDayOfMonth: SelectedDayOfMonth,
  getDayInfo: (time: number) => {dayOfMonth: number; dayOfWeek: number},
  getNextDay: (time: number) => number
): number => {
  const {dayNumber} = selectedDayOfMonth
  let {dayOfMonth, dayOfWeek} = getDayInfo(time)

  let lastTimeSaved = dayOfWeek < 5 && dayNumber === 'last' ? time : 0
  const numberOfWeekdays = (dayOfMonth: number, dayOfWeek: number): number => {
    if (dayOfWeek >= 5 || dayOfMonth > 7) {
      return -1
    }
    const numberOfWeekendsBeforeTheDay =
      (dayOfMonth - dayOfWeek - 1 > 0 ? 1 : 0) +
      (dayOfMonth - dayOfWeek - 2 > 0 ? 1 : 0)
    return dayOfMonth - numberOfWeekendsBeforeTheDay
  }
  while (
    !(
      (dayNumber !== 'last' &&
        dayNumber === numberOfWeekdays(dayOfMonth, dayOfWeek)) ||
      (lastTimeSaved && isLastDayOfMonth(time))
    )
  ) {
    time = getNextDay(time)
    const info = getDayInfo(time)
    dayOfMonth = info.dayOfMonth
    dayOfWeek = info.dayOfWeek
    if (dayOfWeek < 5 && dayNumber === 'last') {
      lastTimeSaved = time
    }
  }
  return lastTimeSaved || time
}

const getNextSelectedWeekend = (
  time: number,
  selectedDayOfMonth: SelectedDayOfMonth,
  getDayInfo: (time: number) => {dayOfMonth: number; dayOfWeek: number},
  getNextDay: (time: number) => number
): number => {
  const {dayNumber} = selectedDayOfMonth
  let {dayOfMonth, dayOfWeek} = getDayInfo(time)

  let lastTimeSaved = dayOfWeek >= 5 && dayNumber === 'last' ? time : 0
  const numberOfWeekends = (dayOfMonth: number, dayOfWeek: number): number => {
    if (dayOfWeek < 5) {
      return -1
    }
    const sunday = dayOfWeek === 6
    const suts = Math.ceil((dayOfMonth - (sunday ? 1 : 0)) / 7)
    const suns = Math.ceil((dayOfMonth - (sunday ? 0 : 6)) / 7)
    return suts + suns
  }
  while (
    !(
      (dayNumber !== 'last' &&
        dayNumber === numberOfWeekends(dayOfMonth, dayOfWeek)) ||
      (lastTimeSaved && isLastDayOfMonth(time))
    )
  ) {
    time = getNextDay(time)
    const info = getDayInfo(time)
    dayOfMonth = info.dayOfMonth
    dayOfWeek = info.dayOfWeek
    if (dayOfWeek >= 5 && dayNumber === 'last') {
      lastTimeSaved = time
    }
  }
  return lastTimeSaved || time
}

const getNextSelectedSpecificWeekday = (
  time: number,
  selectedDayOfMonth: SelectedDayOfMonth,
  getDayInfo: (time: number) => {dayOfMonth: number; dayOfWeek: number},
  getNextDay: (time: number) => number
): number => {
  const {dayNumber, dayType} = selectedDayOfMonth
  let {dayOfMonth, dayOfWeek} = getDayInfo(time)

  let lastTimeSaved = dayOfWeek === dayType && dayNumber === 'last' ? time : 0
  while (
    !(
      (dayOfWeek === dayType && dayNumber === Math.ceil(dayOfMonth / 7)) ||
      (lastTimeSaved && isLastDayOfMonth(time))
    )
  ) {
    time = getNextDay(time)
    const info = getDayInfo(time)
    dayOfMonth = info.dayOfMonth
    dayOfWeek = info.dayOfWeek
    if (dayOfWeek === dayType && dayNumber === 'last') {
      lastTimeSaved = time
    }
  }
  return lastTimeSaved || time
}

const isLastDayOfMonth = (time: number): boolean => {
  return Time.toLocalDate(time + Time.day).getDate() === 1
}

const getNextDayAfterTheLast = (
  getNextDay: (time: number) => number,
  since: number,
  last: number
): number => {
  let time = since
  while (last >= time) {
    time = getNextDay(time)
  }
  return time
}
